/** Minimal message shape — ChatMessage is compatible. */
export type SaraTurnMessage = {
  role: string | null
  content: string | null
}

/**
 * Phrases sent to MerchGPT when a chip is clicked.
 * Button *labels* can differ (e.g. "Search for products") — never send the label.
 */
export const SARA_QUICK_ACTION_PHRASES = {
  search_products: {
    en: 'Show me products from the catalog',
    sv: 'Visa produkter från katalogen',
  },
  add_to_order_and_create_mockups: {
    en: 'Add to order and create mockups',
    sv: 'Lägg till i ordern och skapa mockups',
  },
  create_mockups: {
    en: 'Create mockups',
    sv: 'Skapa mockups',
  },
  create_mockups_from_logo: {
    en: 'Create mockups from this logo',
    sv: 'Skapa mockups från den här logotypen',
  },
} as const

export const SARA_QUICK_ACTION_LABELS = {
  search_products: {
    en: 'Search for products',
    sv: 'Sök efter produkter',
  },
  add_to_order_and_create_mockups: {
    en: 'Add to order and create mockups',
    sv: 'Lägg till i ordern och skapa mockups',
  },
  create_mockups: {
    en: 'Create mockups',
    sv: 'Skapa mockups',
  },
  create_mockups_from_logo: {
    en: 'Create mockups from this logo',
    sv: 'Skapa mockups från den här logotypen',
  },
} as const

export type SaraQuickActionKind =
  | 'search_products'
  | 'add_to_order_and_create_mockups'
  | 'create_mockups'
  | 'create_mockups_from_logo'
  | 'none'

export const parseSaraTurnContent = (message: SaraTurnMessage) =>
  typeof message.content === 'string'
    ? JSON.parse(message.content || '{}')
    : message.content ?? {}

/** Cart-edit status copy from quote CRUD (visible to guests; system quote_crud is hidden). */
const CART_EDIT_STATUS_RE = /^(Added |Updated |Removed |Order has )/

/** True when this Sara turn already showed catalog products, chips, or cart edits. */
export const saraGroupHasProducts = (messages: SaraTurnMessage[]) =>
  messages.some((message) => {
    const content = parseSaraTurnContent(message)
    if (message.role === 'system') {
      if (content.tool_result === 'product_search') {
        return (content.result?.results?.length ?? 0) > 0
      }
      return content.tool_result === 'quote_crud'
    }
    if (message.role === 'status' && typeof content.message === 'string') {
      return CART_EDIT_STATUS_RE.test(content.message)
    }
    if (message.role === 'assistant' && typeof content.message === 'string') {
      return /\[product\]/.test(content.message)
    }
    return false
  })

/** True when this Sara turn already added/updated/removed order lines. */
export const saraGroupHasCartEdits = (messages: SaraTurnMessage[]) =>
  messages.some((message) => {
    const content = parseSaraTurnContent(message)
    if (message.role === 'system') return content.tool_result === 'quote_crud'
    if (message.role === 'status' && typeof content.message === 'string') {
      return CART_EDIT_STATUS_RE.test(content.message)
    }
    return false
  })

export const saraGroupHasMockups = (messages: SaraTurnMessage[]) =>
  messages.some((message) => {
    if (message.role !== 'system') return false
    return parseSaraTurnContent(message).tool_result === 'mockup_generation'
  })

export const saraGroupFetchedLogoUrls = (
  messages: SaraTurnMessage[]
): string[] => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message.role !== 'system') continue
    const content = parseSaraTurnContent(message)
    if (content.tool_result !== 'logo_fetch') continue
    const logos = Array.isArray(content.data?.logos) ? content.data.logos : []
    return logos.filter((url: unknown): url is string => typeof url === 'string')
  }
  return []
}

/**
 * Which chip the latest Sara turn should offer.
 * Search = advice only. Add+mockups = products recommended, not in the order.
 * Create mockups = products already on the order. Logo fetch = pick a logo.
 * None = mockups already ran.
 */
/** Latest uninterrupted Sara turn (excludes trailing customer messages). */
export const getLatestSaraGroupMessages = (
  messages: SaraTurnMessage[]
): SaraTurnMessage[] => {
  const group: SaraTurnMessage[] = []
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message.role === 'user') break
    group.unshift(message)
  }
  return group
}

export const extractAssistantQuickReply = (
  messages: SaraTurnMessage[]
): { label: string; phrase: string } | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message.role !== 'assistant') continue
    const content = parseSaraTurnContent(message)
    const reply = content.quick_reply
    if (
      reply &&
      typeof reply.label === 'string' &&
      reply.label.trim() &&
      typeof reply.phrase === 'string' &&
      reply.phrase.trim()
    ) {
      return { label: reply.label.trim(), phrase: reply.phrase.trim() }
    }
  }
  return null
}

export const resolveSaraQuickAction = (
  messages: SaraTurnMessage[]
): SaraQuickActionKind => {
  const hasAssistantReply = messages.some((m) => m.role === 'assistant')
  const hasProducts = saraGroupHasProducts(messages)
  const hasMockups = saraGroupHasMockups(messages)
  const hasCartEdits = saraGroupHasCartEdits(messages)
  const fetchedLogoUrls = saraGroupFetchedLogoUrls(messages)

  if (fetchedLogoUrls.length > 0 && !hasMockups) {
    return 'create_mockups_from_logo'
  }
  if (hasAssistantReply && !hasProducts && !hasMockups) return 'search_products'
  if (hasProducts && !hasMockups && !hasCartEdits) {
    return 'add_to_order_and_create_mockups'
  }
  if (hasProducts && !hasMockups && hasCartEdits) return 'create_mockups'
  return 'none'
}
